/**
 * ConcoursForm - Form for creating/editing concours
 * Part 1: Main form component with basic fields
 */
import {
  AlertCircle,
  ArrowLeft,
  BookmarkCheck,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  Plus,
  Save,
  Sparkles,
  Trash2,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/api';
import { concoursApiClient } from '../../services/concoursApi';
import {
  Concours,
  CONCOURS_CATEGORIES,
  CONCOURS_LEVELS,
  CONCOURS_STATUS_LABELS,
  ConcoursCategory,
  ConcoursCreateRequest,
  ConcoursLevel,
  ConcoursStatus,
  ConcoursType
} from '../../types/concours';
import { migrateCalendar } from '../../utils/calendarMigration';

interface ConcoursFormProps {
  concours?: Concours;
  onSuccess: () => void;
  onCancel: () => void;
}

// Section collapse state type
type SectionKey = 'basic' | 'classification' | 'eligibility' | 'calendar' | 'steps' | 'fees' | 'documents' | 'links' | 'settings';

const ConcoursForm: React.FC<ConcoursFormProps> = ({ concours, onSuccess, onCancel }) => {
  const isEditing = !!concours;

  // Form state
  const [formData, setFormData] = useState<ConcoursCreateRequest>({
    slug: '',
    title: '',
    subtitle: '',
    description: '',
    institution: '',
    organizing_body: '',
    year: new Date().getFullYear() + 1,
    category: 'Éducation',
    subcategory: '',
    level: 'BAC',
    type: 'direct',
    is_regionalized: false,
    eligibility: {
      nationality: 'Ivoirienne',
      min_age: 18,
      max_age: 35,
      required_diplomas: [],
      other_conditions: [],
    },
    required_documents: [],
    calendar: { events: [] },
    registration_steps: [],
    registration_fee: 0,
    medical_exam_fee: 0,
    payment_methods: [],
    links: {},
    contacts: [],
    status: 'draft',
    is_featured: false,
    is_popular: false,
    priority: 0,
    keywords: [],
    tips: [],
    warnings: [],
  });

  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // AI import panel
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiUrl, setAiUrl] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiPdf, setAiPdf] = useState<File | null>(null);

  const handleAIExtract = async () => {
    if (!aiText.trim() && !aiUrl.trim() && !aiPdf) {
      setAiError('Collez un texte, entrez une URL ou sélectionnez un PDF.');
      return;
    }
    setAiLoading(true);
    setAiError(null);
    try {
      const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      let res: Response;
      if (aiPdf) {
        const fd = new FormData();
        fd.append('file', aiPdf);
        res = await authFetch(`${apiBase}/api/pka/extract-pdf`, { method: 'POST', body: fd });
      } else {
        res = await authFetch(`${apiBase}/api/pka/extract`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: aiText.trim() || undefined,
            url: aiUrl.trim() || undefined,
          }),
        });
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Erreur ${res.status}`);
      }
      const { data } = await res.json();

      // ── Normalisation : l'IA peut retourner des formats simplifiés ──

      // required_documents: string[] → RequiredDocumentCategory[]
      if (Array.isArray(data.required_documents) && data.required_documents.length > 0
        && typeof data.required_documents[0] === 'string') {
        data.required_documents = [{
          category: 'Documents requis',
          documents: (data.required_documents as string[]).map(name => ({ name, type: 'original' as const })),
        }];
      }

      // eligibility.required_diplomas: string[] → RequiredDiploma[]
      if (Array.isArray(data.eligibility?.required_diplomas)
        && data.eligibility.required_diplomas.length > 0
        && typeof data.eligibility.required_diplomas[0] === 'string') {
        data.eligibility.required_diplomas = (data.eligibility.required_diplomas as string[])
          .map(d => ({ name: d, full_name: d }));
      }

      // contacts: {name, phone, email, role}[] → Contact[]
      if (Array.isArray(data.contacts)) {
        data.contacts = data.contacts.flatMap((c: any) => {
          // Already in correct format
          if (c.type && c.value) return [c];
          // Convert legacy {name, phone, email, role} format
          const items: any[] = [];
          if (c.phone) items.push({ type: 'phone', label: c.name || c.role || undefined, value: c.phone });
          if (c.whatsapp) items.push({ type: 'whatsapp', label: c.name || c.role || undefined, value: c.whatsapp });
          if (c.email) items.push({ type: 'email', label: c.name || c.role || undefined, value: c.email });
          if (c.address) items.push({ type: 'address', label: c.name || undefined, value: c.address });
          return items;
        }).filter((c: any) => c.value);
      }

      // ── Fusion dans le formulaire ──
      setFormData(prev => ({
        ...prev,
        ...(data.title && { title: data.title }),
        ...(data.subtitle && { subtitle: data.subtitle }),
        ...(data.description && { description: data.description }),
        ...(data.institution && { institution: data.institution }),
        ...(data.organizing_body && { organizing_body: data.organizing_body }),
        ...(data.year && { year: data.year }),
        ...(data.category && { category: data.category }),
        ...(data.subcategory && { subcategory: data.subcategory }),
        ...(data.level && { level: data.level }),
        ...(data.type && { type: data.type }),
        ...(data.registration_fee != null && { registration_fee: data.registration_fee }),
        ...(data.medical_exam_fee != null && { medical_exam_fee: data.medical_exam_fee }),
        ...(data.other_fees?.length && { other_fees: data.other_fees }),
        ...(data.required_documents?.length && { required_documents: data.required_documents }),
        ...(data.keywords?.length && { keywords: data.keywords }),
        ...(data.tips?.length && { tips: data.tips }),
        ...(data.warnings?.length && { warnings: data.warnings }),
        ...(data.contacts?.length && { contacts: data.contacts }),
        ...(data.links && { links: { ...prev.links, ...data.links } }),
        ...(data.calendar?.events?.length && { calendar: data.calendar }),
        ...(data.registration_steps?.length && { registration_steps: data.registration_steps }),
        ...(data.exams?.length && { exams: data.exams }),
        eligibility: {
          ...prev.eligibility,
          ...(data.eligibility || {}),
        },
        slug: isEditing ? prev.slug : (data.title
          ? concoursApiClient.generateSlug(data.title, data.year || prev.year)
          : prev.slug),
      }));

      // Ouvrir toutes les sections pour que l'utilisateur voit les données importées
      setExpandedSections({
        basic: true,
        classification: true,
        eligibility: true,
        calendar: true,
        steps: true,
        fees: true,
        documents: true,
        links: true,
        settings: true,
      });

      setAiPanelOpen(false);
      setAiText('');
      setAiUrl('');
      setAiPdf(null);
    } catch (err: any) {
      setAiError(err.message || 'Erreur inconnue');
    } finally {
      setAiLoading(false);
    }
  };

  // Section collapse state
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    basic: true,
    classification: true,
    eligibility: false,
    calendar: false,
    steps: false,
    fees: false,
    documents: false,
    links: false,
    settings: false,
  });

  // Initialize form with existing data
  useEffect(() => {
    if (concours) {
      setFormData({
        slug: concours.slug,
        title: concours.title,
        subtitle: concours.subtitle || '',
        description: concours.description || '',
        institution: concours.institution,
        organizing_body: concours.organizing_body || '',
        year: concours.year,
        category: concours.category,
        subcategory: concours.subcategory || '',
        level: concours.level,
        type: concours.type,
        is_regionalized: concours.is_regionalized || false,
        eligibility: concours.eligibility || {
          nationality: 'Ivoirienne',
          min_age: 18,
          max_age: 35,
          required_diplomas: [],
          other_conditions: [],
        },
        required_documents: concours.required_documents || [],
        calendar: migrateCalendar(concours.calendar, concours) || { events: [] },
        registration_steps: concours.registration_steps || [],
        registration_fee: concours.registration_fee || 0,
        medical_exam_fee: concours.medical_exam_fee || 0,
        payment_methods: concours.payment_methods || [],
        links: concours.links || {},
        contacts: concours.contacts || [],
        status: concours.status,
        is_featured: concours.is_featured || false,
        is_popular: concours.is_popular || false,
        priority: concours.priority || 0,
        keywords: concours.keywords || [],
        tips: concours.tips || [],
        warnings: concours.warnings || [],
      });
    }
  }, [concours]);

  // Auto-generate slug from title
  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: isEditing ? prev.slug : concoursApiClient.generateSlug(title, prev.year),
    }));
  };

  // Toggle section
  const toggleSection = (section: SectionKey) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors([]);

    // Validate
    const errors = concoursApiClient.validateConcours(formData);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);

    try {
      if (isEditing && concours) {
        await concoursApiClient.updateConcours(concours.id, formData);
      } else {
        await concoursApiClient.createConcours(formData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  // Save as draft (skip validation, force status=draft)
  const handleSaveDraft = async () => {
    if (!formData.title?.trim()) {
      setError('Un titre est requis pour sauvegarder en brouillon.');
      return;
    }
    setError(null);
    setSavingDraft(true);
    try {
      const draftData = { ...formData, status: 'draft' as const };
      if (!draftData.slug) {
        draftData.slug = concoursApiClient.generateSlug(
          draftData.title,
          draftData.year || new Date().getFullYear()
        );
      }
      if (!draftData.institution) draftData.institution = 'À compléter';
      if (!draftData.year) draftData.year = new Date().getFullYear();
      if (!draftData.category) draftData.category = 'Administration';
      if (isEditing && concours) {
        await concoursApiClient.updateConcours(concours.id, draftData);
      } else {
        await concoursApiClient.createConcours(draftData);
      }
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Erreur sauvegarde brouillon');
    } finally {
      setSavingDraft(false);
    }
  };

  // Section header component
  const SectionHeader: React.FC<{ title: string; section: SectionKey; icon?: React.ReactNode }> = ({
    title,
    section,
    icon
  }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition rounded-t-lg"
    >
      <div className="flex items-center gap-2 font-medium text-gray-900">
        {icon}
        {title}
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-gray-500" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-500" />
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onCancel}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Modifier le concours' : 'Nouveau concours'}
            </h1>
            <p className="text-gray-600">
              {isEditing ? `Modification de "${concours.title}"` : 'Créer un nouveau concours'}
            </p>
          </div>
        </div>
        {/* AI import button */}
        <button
          type="button"
          onClick={() => { setAiPanelOpen(true); setAiError(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition font-medium text-sm"
        >
          <Sparkles className="w-4 h-4" />
          Importer avec l'IA
        </button>
      </div>

      {/* AI import panel */}
      {aiPanelOpen && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold text-violet-900">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Pré-remplir depuis un texte ou une URL
            </div>
            <button
              type="button"
              onClick={() => { setAiPanelOpen(false); setAiText(''); setAiUrl(''); setAiPdf(null); setAiError(null); }}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-violet-700">
            Collez le texte de l'annonce, entrez l'URL de la page officielle (supporte les liens PDF)
            ou uploadez directement un fichier PDF. L'IA pré-remplira le formulaire automatiquement.
          </p>
          {/* PDF file upload */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fichier PDF</label>
            <label className="flex items-center gap-2 w-full border-2 border-dashed border-violet-200 rounded-lg px-3 py-3 cursor-pointer hover:border-violet-400 transition">
              <FileText className="w-4 h-4 text-violet-500 flex-shrink-0" />
              <span className="text-sm text-gray-600 truncate flex-1">
                {aiPdf ? aiPdf.name : 'Cliquez pour sélectionner un PDF…'}
              </span>
              {aiPdf && (
                <button
                  type="button"
                  onClick={e => { e.preventDefault(); setAiPdf(null); }}
                  className="text-gray-400 hover:text-red-500 transition flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <input
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={e => setAiPdf(e.target.files?.[0] || null)}
              />
            </label>
          </div>
          {/* URL input */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">URL de la page officielle ou lien PDF (optionnel)</label>
            <input
              type="url"
              value={aiUrl}
              onChange={e => setAiUrl(e.target.value)}
              placeholder="https://..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>
          {/* Text area */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Texte de l'annonce (copier-coller)</label>
            <textarea
              value={aiText}
              onChange={e => setAiText(e.target.value)}
              rows={6}
              placeholder="Collez ici le contenu de l'annonce du concours…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-y"
            />
          </div>
          {aiError && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {aiError}
            </p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleAIExtract}
              disabled={aiLoading}
              className="flex items-center gap-2 px-5 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded-lg transition font-medium text-sm"
            >
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {aiLoading ? 'Analyse en cours…' : 'Analyser et pré-remplir'}
            </button>
          </div>
        </div>
      )}

      {/* Error messages */}
      {(error || validationErrors.length > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              {error && <p className="text-red-700 font-medium">{error}</p>}
              {validationErrors.length > 0 && (
                <ul className="list-disc list-inside text-red-600 text-sm mt-1">
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <SectionHeader title="Informations de base" section="basic" />
          {expandedSections.basic && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ex: Concours direct d'entrée dans les CAFOP-IA"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                    placeholder="cafop-ia-2026"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">URL-friendly, lettres minuscules et tirets uniquement</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Année <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min={2024}
                    max={2030}
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sous-titre</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ex: Centres d'Animation et de Formation Pédagogique"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Institution <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.institution}
                    onChange={(e) => setFormData(prev => ({ ...prev, institution: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ex: Ministère de l'Éducation Nationale"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organisme organisateur</label>
                  <input
                    type="text"
                    value={formData.organizing_body}
                    onChange={(e) => setFormData(prev => ({ ...prev, organizing_body: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ex: DECO - Direction des Examens et Concours"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={4}
                    placeholder="Description détaillée du concours..."
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Classification */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <SectionHeader title="Classification" section="classification" />
          {expandedSections.classification && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as ConcoursCategory }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    {CONCOURS_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sous-catégorie</label>
                  <input
                    type="text"
                    value={formData.subcategory}
                    onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ex: Enseignement primaire"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Niveau
                  </label>
                  <select
                    value={formData.level || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value as ConcoursLevel || undefined }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">-- Sélectionner un niveau --</option>
                    {CONCOURS_LEVELS.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choisir "Professionnel" pour les concours basés sur des grades (ex: ACE)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de concours</label>
                  <select
                    value={formData.type || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as ConcoursType || undefined }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Non spécifié</option>
                    <option value="direct">Concours direct</option>
                    <option value="professionnel">Concours professionnel</option>
                    <option value="interne">Concours interne</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_regionalized || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_regionalized: e.target.checked }))}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Concours régionalisé</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Eligibility */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <SectionHeader title="Conditions d'éligibilité" section="eligibility" />
          {expandedSections.eligibility && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nationalité</label>
                  <input
                    type="text"
                    value={formData.eligibility?.nationality || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      eligibility: { ...prev.eligibility!, nationality: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ivoirienne"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Âge minimum</label>
                  <input
                    type="number"
                    value={formData.eligibility?.min_age || 18}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      eligibility: { ...prev.eligibility!, min_age: parseInt(e.target.value) }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min={15}
                    max={60}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Âge maximum</label>
                  <input
                    type="number"
                    value={formData.eligibility?.max_age || 35}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      eligibility: { ...prev.eligibility!, max_age: parseInt(e.target.value) }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min={15}
                    max={65}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de référence pour l'âge</label>
                  <input
                    type="date"
                    value={formData.eligibility?.age_reference_date || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      eligibility: { ...prev.eligibility!, age_reference_date: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note sur l'âge</label>
                  <input
                    type="text"
                    value={formData.eligibility?.age_note || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      eligibility: { ...prev.eligibility!, age_note: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ex: Être né(e) entre 1987 et 2007"
                  />
                </div>
              </div>

              {/* Diplômes requis */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Diplômes requis</h4>
                <div className="space-y-3">
                  {(formData.eligibility?.required_diplomas || []).map((diploma, index) => (
                    <div key={index} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={diploma.name || ''}
                            onChange={(e) => {
                              const newDiplomas = [...(formData.eligibility?.required_diplomas || [])];
                              newDiplomas[index] = { ...newDiplomas[index], name: e.target.value };
                              setFormData(prev => ({
                                ...prev,
                                eligibility: { ...prev.eligibility!, required_diplomas: newDiplomas }
                              }));
                            }}
                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Sigle (ex: BEPC)"
                          />
                          <input
                            type="text"
                            value={diploma.full_name || ''}
                            onChange={(e) => {
                              const newDiplomas = [...(formData.eligibility?.required_diplomas || [])];
                              newDiplomas[index] = { ...newDiplomas[index], full_name: e.target.value };
                              setFormData(prev => ({
                                ...prev,
                                eligibility: { ...prev.eligibility!, required_diplomas: newDiplomas }
                              }));
                            }}
                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Nom complet"
                          />
                        </div>
                        <input
                          type="text"
                          value={(diploma.alternatives || []).join(', ')}
                          onChange={(e) => {
                            const newDiplomas = [...(formData.eligibility?.required_diplomas || [])];
                            newDiplomas[index] = {
                              ...newDiplomas[index],
                              alternatives: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                            };
                            setFormData(prev => ({
                              ...prev,
                              eligibility: { ...prev.eligibility!, required_diplomas: newDiplomas }
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Alternatives (séparées par des virgules)"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newDiplomas = (formData.eligibility?.required_diplomas || []).filter((_, i) => i !== index);
                          setFormData(prev => ({
                            ...prev,
                            eligibility: { ...prev.eligibility!, required_diplomas: newDiplomas }
                          }));
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => {
                      const newDiplomas = [
                        ...(formData.eligibility?.required_diplomas || []),
                        { name: '', full_name: '', alternatives: [] }
                      ];
                      setFormData(prev => ({
                        ...prev,
                        eligibility: { ...prev.eligibility!, required_diplomas: newDiplomas }
                      }));
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter un diplôme requis
                  </button>
                </div>
              </div>

              {/* Autres conditions */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Autres conditions</h4>
                <textarea
                  value={(formData.eligibility?.other_conditions || []).join('\n')}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    eligibility: {
                      ...prev.eligibility!,
                      other_conditions: e.target.value.split('\n').filter(Boolean)
                    }
                  }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[150px]"
                  placeholder="Une condition par ligne&#10;Exemple:&#10;Être Professeur Agrégé ou Inspecteur Principal grade A5&#10;N'avoir fait l'objet d'aucune sanction disciplinaire&#10;Maîtriser l'outil informatique (Word, Excel, PowerPoint)"
                />
                <p className="text-xs text-gray-500 mt-1">Une condition par ligne</p>
              </div>
            </div>
          )}
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <SectionHeader title="Calendrier" section="calendar" />
          {expandedSections.calendar && (
            <div className="p-6 space-y-4">
              <div className="space-y-4">
                {(formData.calendar?.events || []).map((event, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Libellé de l'événement</label>
                          <input
                            type="text"
                            value={event.label}
                            onChange={(e) => {
                              const newEvents = [...(formData.calendar?.events || [])];
                              newEvents[index] = { ...newEvents[index], label: e.target.value };
                              setFormData(prev => ({
                                ...prev,
                                calendar: { events: newEvents }
                              }));
                            }}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Ex: PRÉINSCRIPTION, PAIEMENT DU DROIT D'INSCRIPTION, etc."
                          />
                        </div>

                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              checked={event.type === 'period' || (!event.type && !!event.start)}
                              onChange={() => {
                                const newEvents = [...(formData.calendar?.events || [])];
                                newEvents[index] = { ...newEvents[index], type: 'period', date: undefined };
                                setFormData(prev => ({
                                  ...prev,
                                  calendar: { events: newEvents }
                                }));
                              }}
                              className="text-indigo-600"
                            />
                            <span className="text-sm text-gray-700">Période</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              checked={event.type === 'date' || (!event.type && !!event.date)}
                              onChange={() => {
                                const newEvents = [...(formData.calendar?.events || [])];
                                newEvents[index] = { ...newEvents[index], type: 'date', start: undefined, end: undefined };
                                setFormData(prev => ({
                                  ...prev,
                                  calendar: { events: newEvents }
                                }));
                              }}
                              className="text-indigo-600"
                            />
                            <span className="text-sm text-gray-700">Date unique</span>
                          </label>
                        </div>

                        {(event.type === 'period' || (!event.type && event.start)) ? (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Début</label>
                              <input
                                type="date"
                                value={event.start || ''}
                                onChange={(e) => {
                                  const newEvents = [...(formData.calendar?.events || [])];
                                  newEvents[index] = { ...newEvents[index], start: e.target.value };
                                  setFormData(prev => ({
                                    ...prev,
                                    calendar: { events: newEvents }
                                  }));
                                }}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Fin</label>
                              <input
                                type="date"
                                value={event.end || ''}
                                onChange={(e) => {
                                  const newEvents = [...(formData.calendar?.events || [])];
                                  newEvents[index] = { ...newEvents[index], end: e.target.value };
                                  setFormData(prev => ({
                                    ...prev,
                                    calendar: { events: newEvents }
                                  }));
                                }}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                            </div>
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Date</label>
                            <input
                              type="date"
                              value={event.date || ''}
                              onChange={(e) => {
                                const newEvents = [...(formData.calendar?.events || [])];
                                newEvents[index] = { ...newEvents[index], date: e.target.value };
                                setFormData(prev => ({
                                  ...prev,
                                  calendar: { events: newEvents }
                                }));
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          const newEvents = (formData.calendar?.events || []).filter((_, i) => i !== index);
                          setFormData(prev => ({
                            ...prev,
                            calendar: { events: newEvents }
                          }));
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    const newEvents = [
                      ...(formData.calendar?.events || []),
                      { label: '', type: 'period' as const, start: '', end: '' }
                    ];
                    setFormData(prev => ({
                      ...prev,
                      calendar: { events: newEvents }
                    }));
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition"
                >
                  <Plus className="w-5 h-5" />
                  Ajouter un événement au calendrier
                </button>

                {(!formData.calendar?.events || formData.calendar.events.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">Aucun événement ajouté.</p>
                    <p className="text-xs mt-1">Cliquez sur le bouton ci-dessus pour commencer.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Registration Steps */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <SectionHeader title="Étapes du parcours d'inscription" section="steps" />
          {expandedSections.steps && (
            <div className="p-6 space-y-4">
              <div className="space-y-4">
                {(formData.registration_steps || []).map((step, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-3">
                        {/* Order & Name */}
                        <div className="grid grid-cols-12 gap-3">
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-500 mb-1">Ordre</label>
                            <input
                              type="number"
                              value={step.order}
                              onChange={(e) => {
                                const newSteps = [...(formData.registration_steps || [])];
                                newSteps[index] = { ...newSteps[index], order: parseInt(e.target.value) || 1 };
                                setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              min={1}
                            />
                          </div>
                          <div className="col-span-10">
                            <label className="block text-xs text-gray-500 mb-1">Nom de l'étape</label>
                            <input
                              type="text"
                              value={step.name}
                              onChange={(e) => {
                                const newSteps = [...(formData.registration_steps || [])];
                                newSteps[index] = { ...newSteps[index], name: e.target.value };
                                setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="Ex: Vérifier éligibilité"
                            />
                          </div>
                        </div>

                        {/* Period */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Période</label>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="date"
                              value={step.period?.start || ''}
                              onChange={(e) => {
                                const newSteps = [...(formData.registration_steps || [])];
                                newSteps[index] = {
                                  ...newSteps[index],
                                  period: { ...newSteps[index].period, start: e.target.value, end: newSteps[index].period?.end || '' }
                                };
                                setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="Début"
                            />
                            <input
                              type="date"
                              value={step.period?.end || ''}
                              onChange={(e) => {
                                const newSteps = [...(formData.registration_steps || [])];
                                newSteps[index] = {
                                  ...newSteps[index],
                                  period: { start: newSteps[index].period?.start || '', end: e.target.value }
                                };
                                setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="Fin"
                            />
                          </div>
                        </div>

                        {/* Website & Location */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Site web</label>
                            <input
                              type="url"
                              value={step.website || ''}
                              onChange={(e) => {
                                const newSteps = [...(formData.registration_steps || [])];
                                newSteps[index] = { ...newSteps[index], website: e.target.value };
                                setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="www.men-deco.org"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Lieu</label>
                            <input
                              type="text"
                              value={step.location || ''}
                              onChange={(e) => {
                                const newSteps = [...(formData.registration_steps || [])];
                                newSteps[index] = { ...newSteps[index], location: e.target.value };
                                setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="DRENA, Bureau de La Poste..."
                            />
                          </div>
                        </div>

                        {/* Substeps (Tasks) */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Tâches à accomplir ({(step.substeps || []).length})
                          </label>
                          <div className="space-y-2">
                            {(step.substeps || []).map((substep, subIndex) => (
                              <div key={subIndex} className="flex gap-2">
                                <input
                                  type="text"
                                  value={substep}
                                  onChange={(e) => {
                                    const newSteps = [...(formData.registration_steps || [])];
                                    const newSubsteps = [...(newSteps[index].substeps || [])];
                                    newSubsteps[subIndex] = e.target.value;
                                    newSteps[index] = { ...newSteps[index], substeps: newSubsteps };
                                    setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                                  }}
                                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  placeholder="Ex: Vérifier la nationalité ivoirienne"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newSteps = [...(formData.registration_steps || [])];
                                    const newSubsteps = (newSteps[index].substeps || []).filter((_, i) => i !== subIndex);
                                    newSteps[index] = { ...newSteps[index], substeps: newSubsteps };
                                    setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                                  }}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const newSteps = [...(formData.registration_steps || [])];
                                const newSubsteps = [...(newSteps[index].substeps || []), ''];
                                newSteps[index] = { ...newSteps[index], substeps: newSubsteps };
                                setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                              }}
                              className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition"
                            >
                              + Ajouter une tâche
                            </button>
                          </div>
                        </div>

                        {/* Objective & Warning */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Objectif</label>
                            <input
                              type="text"
                              value={step.objective || ''}
                              onChange={(e) => {
                                const newSteps = [...(formData.registration_steps || [])];
                                newSteps[index] = { ...newSteps[index], objective: e.target.value };
                                setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="S'assurer que je remplis toutes les conditions"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Avertissement</label>
                            <input
                              type="text"
                              value={step.warning || ''}
                              onChange={(e) => {
                                const newSteps = [...(formData.registration_steps || [])];
                                newSteps[index] = { ...newSteps[index], warning: e.target.value };
                                setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="Attention: documents à préparer à l'avance"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => {
                          const newSteps = (formData.registration_steps || []).filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Supprimer l'étape"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    const newSteps = [
                      ...(formData.registration_steps || []),
                      {
                        order: (formData.registration_steps || []).length + 1,
                        name: '',
                        period: { start: '', end: '' },
                        website: '',
                        location: '',
                        substeps: [],
                        objective: '',
                        warning: ''
                      }
                    ];
                    setFormData(prev => ({ ...prev, registration_steps: newSteps }));
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition"
                >
                  <Plus className="w-5 h-5" />
                  Ajouter une étape du parcours
                </button>

                {(!formData.registration_steps || formData.registration_steps.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">Aucune étape ajoutée.</p>
                    <p className="text-xs mt-1">Les étapes guident le candidat dans son parcours d'inscription.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Fees */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <SectionHeader title="Frais" section="fees" />
          {expandedSections.fees && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frais d'inscription (FCFA)</label>
                  <input
                    type="number"
                    value={formData.registration_fee || 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, registration_fee: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min={0}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frais visite médicale (FCFA)</label>
                  <input
                    type="number"
                    value={formData.medical_exam_fee || 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, medical_exam_fee: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min={0}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Moyens de paiement</label>
                  <input
                    type="text"
                    value={formData.payment_methods?.join(', ') || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      payment_methods: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Orange Money, MTN Money, Wave, Trésor Public"
                  />
                  <p className="text-xs text-gray-500 mt-1">Séparez par des virgules</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Documents Requis */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <SectionHeader title="Documents requis" section="documents" />
          {expandedSections.documents && (
            <div className="p-6">
              <div className="space-y-6">
                {(formData.required_documents || []).map((category, categoryIndex) => (
                  <div key={categoryIndex} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    {/* Category header */}
                    <div className="flex items-center gap-3 mb-4">
                      <input
                        type="text"
                        value={category.category}
                        onChange={(e) => {
                          const newDocs = [...(formData.required_documents || [])];
                          newDocs[categoryIndex] = { ...newDocs[categoryIndex], category: e.target.value };
                          setFormData(prev => ({ ...prev, required_documents: newDocs }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Nom de la catégorie (ex: Documents administratifs)"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newDocs = (formData.required_documents || []).filter((_, i) => i !== categoryIndex);
                          setFormData(prev => ({ ...prev, required_documents: newDocs }));
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Documents in this category */}
                    <div className="space-y-3 ml-4">
                      {(category.documents || []).map((doc, docIndex) => (
                        <div key={docIndex} className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                          <div className="flex items-start gap-2">
                            <div className="flex-1 grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={doc.name}
                                onChange={(e) => {
                                  const newDocs = [...(formData.required_documents || [])];
                                  const newDocList = [...newDocs[categoryIndex].documents];
                                  newDocList[docIndex] = { ...newDocList[docIndex], name: e.target.value };
                                  newDocs[categoryIndex] = { ...newDocs[categoryIndex], documents: newDocList };
                                  setFormData(prev => ({ ...prev, required_documents: newDocs }));
                                }}
                                className="col-span-2 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Nom du document"
                              />
                              <select
                                value={doc.type}
                                onChange={(e) => {
                                  const newDocs = [...(formData.required_documents || [])];
                                  const newDocList = [...newDocs[categoryIndex].documents];
                                  newDocList[docIndex] = { ...newDocList[docIndex], type: e.target.value as any };
                                  newDocs[categoryIndex] = { ...newDocs[categoryIndex], documents: newDocList };
                                  setFormData(prev => ({ ...prev, required_documents: newDocs }));
                                }}
                                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              >
                                <option value="original">Original</option>
                                <option value="copie">Copie</option>
                                <option value="imprimé">Imprimé</option>
                                <option value="fourniture">Fourniture</option>
                              </select>
                              <input
                                type="text"
                                value={doc.validity || ''}
                                onChange={(e) => {
                                  const newDocs = [...(formData.required_documents || [])];
                                  const newDocList = [...newDocs[categoryIndex].documents];
                                  newDocList[docIndex] = { ...newDocList[docIndex], validity: e.target.value };
                                  newDocs[categoryIndex] = { ...newDocs[categoryIndex], documents: newDocList };
                                  setFormData(prev => ({ ...prev, required_documents: newDocs }));
                                }}
                                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Validité (optionnel)"
                              />
                              <input
                                type="text"
                                value={doc.note || ''}
                                onChange={(e) => {
                                  const newDocs = [...(formData.required_documents || [])];
                                  const newDocList = [...newDocs[categoryIndex].documents];
                                  newDocList[docIndex] = { ...newDocList[docIndex], note: e.target.value };
                                  newDocs[categoryIndex] = { ...newDocs[categoryIndex], documents: newDocList };
                                  setFormData(prev => ({ ...prev, required_documents: newDocs }));
                                }}
                                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Note (optionnel)"
                              />
                              <input
                                type="text"
                                value={doc.source || ''}
                                onChange={(e) => {
                                  const newDocs = [...(formData.required_documents || [])];
                                  const newDocList = [...newDocs[categoryIndex].documents];
                                  newDocList[docIndex] = { ...newDocList[docIndex], source: e.target.value };
                                  newDocs[categoryIndex] = { ...newDocs[categoryIndex], documents: newDocList };
                                  setFormData(prev => ({ ...prev, required_documents: newDocs }));
                                }}
                                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Source (optionnel)"
                              />
                              <input
                                type="number"
                                value={doc.amount || ''}
                                onChange={(e) => {
                                  const newDocs = [...(formData.required_documents || [])];
                                  const newDocList = [...newDocs[categoryIndex].documents];
                                  newDocList[docIndex] = { ...newDocList[docIndex], amount: parseInt(e.target.value) || undefined };
                                  newDocs[categoryIndex] = { ...newDocs[categoryIndex], documents: newDocList };
                                  setFormData(prev => ({ ...prev, required_documents: newDocs }));
                                }}
                                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Montant (optionnel)"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const newDocs = [...(formData.required_documents || [])];
                                const newDocList = newDocs[categoryIndex].documents.filter((_, i) => i !== docIndex);
                                newDocs[categoryIndex] = { ...newDocs[categoryIndex], documents: newDocList };
                                setFormData(prev => ({ ...prev, required_documents: newDocs }));
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() => {
                          const newDocs = [...(formData.required_documents || [])];
                          const newDocList = [
                            ...newDocs[categoryIndex].documents,
                            { name: '', type: 'original' as const }
                          ];
                          newDocs[categoryIndex] = { ...newDocs[categoryIndex], documents: newDocList };
                          setFormData(prev => ({ ...prev, required_documents: newDocs }));
                        }}
                        className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition"
                      >
                        + Ajouter un document dans cette catégorie
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    const newDocs = [
                      ...(formData.required_documents || []),
                      { category: '', documents: [] }
                    ];
                    setFormData(prev => ({ ...prev, required_documents: newDocs }));
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition"
                >
                  <Plus className="w-5 h-5" />
                  Ajouter une catégorie de documents
                </button>

                {(!formData.required_documents || formData.required_documents.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">Aucun document requis ajouté.</p>
                    <p className="text-xs mt-1">Organisez les documents par catégories (administratifs, académiques, etc.)</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Links & Contacts */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <SectionHeader title="Liens & Contacts" section="links" />
          {expandedSections.links && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site officiel</label>
                  <input
                    type="url"
                    value={formData.links?.official || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      links: { ...prev.links, official: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://www.men-deco.org"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inscription</label>
                  <input
                    type="url"
                    value={formData.links?.registration || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      links: { ...prev.links, registration: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://www.men-deco.org/inscription"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Résultats</label>
                  <input
                    type="url"
                    value={formData.links?.results || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      links: { ...prev.links, results: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://www.men-deco.org/resultats"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plus d'infos</label>
                  <input
                    type="url"
                    value={formData.links?.more_info || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      links: { ...prev.links, more_info: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://exemple.com/info"
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Contacts</h4>
                <div className="space-y-3">
                  {(formData.contacts || []).map((contact, index) => (
                    <div key={index} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-3 gap-3">
                        <input
                          type="text"
                          value={contact.label || ''}
                          onChange={(e) => {
                            const newContacts = [...(formData.contacts || [])];
                            newContacts[index] = { ...newContacts[index], label: e.target.value };
                            setFormData(prev => ({ ...prev, contacts: newContacts }));
                          }}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Libellé"
                        />
                        <input
                          type="text"
                          value={contact.value || ''}
                          onChange={(e) => {
                            const newContacts = [...(formData.contacts || [])];
                            newContacts[index] = { ...newContacts[index], value: e.target.value };
                            setFormData(prev => ({ ...prev, contacts: newContacts }));
                          }}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Valeur"
                        />
                        <select
                          value={contact.type || 'phone'}
                          onChange={(e) => {
                            const newContacts = [...(formData.contacts || [])];
                            newContacts[index] = { ...newContacts[index], type: e.target.value as 'phone' | 'email' | 'whatsapp' | 'address' };
                            setFormData(prev => ({ ...prev, contacts: newContacts }));
                          }}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="phone">Téléphone</option>
                          <option value="email">Email</option>
                          <option value="whatsapp">WhatsApp</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newContacts = (formData.contacts || []).filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, contacts: newContacts }));
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => {
                      const newContacts = [
                        ...(formData.contacts || []),
                        { type: 'phone' as const, label: '', value: '', hours: '' }
                      ];
                      setFormData(prev => ({ ...prev, contacts: newContacts }));
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter un contact
                  </button>
                </div>
              </div>

              <div className="border-t pt-4 mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Conseils</h4>
                <textarea
                  value={(formData.tips || []).join('\n')}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    tips: e.target.value.split('\n').filter(Boolean)
                  }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[100px]"
                  placeholder="Un conseil par ligne&#10;Exemple: Commencez par la préinscription dès le 8 décembre&#10;Préparez tous vos documents à l'avance"
                />
                <p className="text-xs text-gray-500 mt-1">Un conseil par ligne</p>
              </div>

              <div className="border-t pt-4 mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Avertissements</h4>
                <textarea
                  value={(formData.warnings || []).join('\n')}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    warnings: e.target.value.split('\n').filter(Boolean)
                  }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[100px]"
                  placeholder="Un avertissement par ligne&#10;Exemple: Aucun remboursement en cas de désistement&#10;Les pièces de mauvaise qualité entraînent le rejet"
                />
                <p className="text-xs text-gray-500 mt-1">Un avertissement par ligne</p>
              </div>
            </div>
          )}
        </div>

        {/* Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <SectionHeader title="Paramètres de publication" section="settings" />
          {expandedSections.settings && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={formData.status || 'draft'}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as ConcoursStatus }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {Object.entries(CONCOURS_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priorité d'affichage</label>
                  <input
                    type="number"
                    value={formData.priority || 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min={0}
                    max={100}
                  />
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_featured || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Mis en avant</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_popular || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_popular: e.target.checked }))}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Populaire</span>
                  </label>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mots-clés</label>
                  <input
                    type="text"
                    value={formData.keywords?.join(', ') || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="CAFOP, enseignant, primaire, BEPC"
                  />
                  <p className="text-xs text-gray-500 mt-1">Séparez par des virgules</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submit buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            Annuler
          </button>
          {/* Save as draft */}
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={savingDraft || loading}
            className="flex items-center gap-2 px-6 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {savingDraft ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <BookmarkCheck className="w-4 h-4" />
            )}
            {savingDraft ? 'Sauvegarde…' : draftSaved ? 'Sauvegardé !' : 'Brouillon'}
          </button>
          <button
            type="submit"
            disabled={loading || savingDraft}
            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditing ? 'Enregistrer les modifications' : 'Créer le concours'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConcoursForm;
